import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonLoading,
  IonPage,
  IonRow,
  IonToast
} from "@ionic/react";
import Header from "../components/Header";
import {ROLE} from "../../enums/ROLE";
import {AuthContext} from "../../providers/authContext";
import {eyeOutline} from "ionicons/icons";
import yoneticiService from "../../services/YoneticiService";
import {formatDateToDDMMYYYY, logError, parseTurkishNumber} from "../../utils";
import {DataTable, DataTablePFSEvent} from "primereact/datatable";
import {Column} from "primereact/column";
import "./YoneticiTahsilatGozlem.css";
import {ColumnGroup} from "primereact/columngroup";
import {Row} from "primereact/row";
import {TahsilatTable} from "../entities/TahsilatTable";
import {Calendar, CalendarValueType} from "primereact/calendar";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";

const YoneticiGelirGozlem: React.FC = () => {
  const { kurumBilgileri, logo } = React.useContext(AuthContext);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [showToastMessage, setShowToastMessage] = useState<string>();
  const [startDate, setStartDate] = useState<CalendarValueType>();
  const [endDate, setEndDate] = useState<CalendarValueType>();
  const [cariFilterValue, setCariFilterValue] = useState<string>('');
  const [gozlemListesi, setGozlemListesi] = useState<TahsilatTable[]>([]);
  const [filtrelenmisGozlemVerisi, setFiltrelenmisGozlemVerisi] = useState(gozlemListesi || []);
  const odemeIzlemeTable = useRef<DataTable>(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10); // varsayılan sayfa boyutu

  const onPageChange = (e: DataTablePFSEvent) => {
    console.log(e);
    setFirst(e.first);
    setRows(e.rows);
  };

  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    setStartDate(firstDay);
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    setEndDate(endDate);
    fetchData(firstDay, endDate);
  }, []);

  const fetchData = (startDate: CalendarValueType, endDate: CalendarValueType) => {
    if (!startDate || !endDate) {
      setShowToastMessage("Başlangıç ve Bitiş Tarihlerini giriniz");
      return
    };
    setShowLoading(true);
    yoneticiService
      .getGelirIzlemeListesi(formatDateToDDMMYYYY(startDate as Date), formatDateToDDMMYYYY(endDate as Date))
      .then((data: TahsilatTable[]) => {
        setGozlemListesi(data);
        setFiltrelenmisGozlemVerisi(data);
      })
      .catch((error: any) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      })
      .finally(() => setShowLoading(false));
  }

  const cariFilter = <InputText value={cariFilterValue}
                                onChange={(e) => {
                                  odemeIzlemeTable.current?.filter(e.target.value, 'adiSoyadi', 'contains');
                                  setCariFilterValue(e.target.value);
                                }} placeholder="Cari Hesap" style={{width: '100%'}}/>

  const sayfaToplami = useMemo(() => {
    const veri = filtrelenmisGozlemVerisi ?? gozlemListesi ?? [];
    return veri
      .slice(first, first + rows)
      .reduce((toplam, item) => toplam + (parseTurkishNumber(item.tutar) || 0), 0);
  }, [filtrelenmisGozlemVerisi, gozlemListesi, first, rows]);

  const genelToplam = useMemo(() => {
    const veri = filtrelenmisGozlemVerisi ?? gozlemListesi ?? [];
    return veri.reduce((toplam, item) => toplam + (parseTurkishNumber(item.tutar) || 0), 0);
  }, [filtrelenmisGozlemVerisi, gozlemListesi]);

  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column footer="TOPLAM" footerStyle={{ textAlign: 'right' }} />
        <Column
          footer={sayfaToplami.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
          footerStyle={{ textAlign: 'right' }}
        />
        <Column footer="" />
      </Row>
      <Row>
        <Column footer="GENEL TOPLAM" footerStyle={{ textAlign: 'right' }} />
        <Column
          footer={genelToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
          footerStyle={{ textAlign: 'right' }}
        />
        <Column footer="" />
      </Row>
    </ColumnGroup>
  );

  return (
    <IonPage>
      <Header
        title={kurumBilgileri?.kisaTanim}
        subtitle=""
        logo={logo}
        roles={[ROLE.Yonetici]}
      />
      <IonContent>
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={"Lütfen Bekleyin..."}
        />
        <IonToast
          isOpen={showToastMessage !== undefined}
          onDidDismiss={() => setShowToastMessage(undefined)}
          message={showToastMessage}
          duration={5000}
          position="top"
        />
        <IonCard>
          <IonCardHeader>
            <IonItem color="light">
              <IonGrid>
                <IonRow className="ion-align-items-center">
                  <IonCol size="5" sizeSm="2"><IonLabel>Başlangıç Tarihi	</IonLabel></IonCol>
                  <IonCol size="5" sizeSm="3">
                    <Calendar id="startDate" locale="tr"
                              dateFormat="dd.mm.yy"
                              value={startDate}
                              onChange={(e) => setStartDate(e.value)} showIcon />
                  </IonCol>
                </IonRow>
                <IonRow className="ion-align-items-center">
                  <IonCol size="5" sizeSm="2"><IonLabel>Bitiş Tarihi	</IonLabel></IonCol>
                  <IonCol size="5" sizeSm="3">
                    <Calendar id="endDate"
                              dateFormat="dd.mm.yy"
                              value={endDate}
                              onChange={(e) => setEndDate(e.value)} showIcon />
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol size="10" sizeSm="5">
                    <IonButton expand="block" onClick={() => fetchData(startDate, endDate)}>Sorgula</IonButton>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonItem>
            <IonItem color="light">
              <IonIcon icon={eyeOutline} slot="start" />
              Gelir İzleme
            </IonItem>
          </IonCardHeader>
          <IonCardContent>
            <DataTable ref={odemeIzlemeTable} value={gozlemListesi}
                       paginator rows={rows} rowsPerPageOptions={[10, 25, 50]} first={first}
                       paginatorTemplate={{
                         // Hangi bileşenlerin hangi sırayla görüneceğini belirler
                         layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown',

                         // RowsPerPageDropdown bileşenini tamamen baştan yazıyoruz
                         'RowsPerPageDropdown': (options) => {
                           return (
                             <div className="flex align-items-center" style={{ gap: '0.5rem', margin: '0 auto' }}>
                               <span style={{ fontSize: '14px', color: '#6c757d' }}>Sayfada</span>

                               <Dropdown
                                 value={options.value}
                                 options={[10, 25, 50]}
                                 onChange={options.onChange}
                                 /* Genişliği kısıtlayan en önemli kısım */
                                 style={{ width: '5rem' }}
                               />

                               <span style={{ fontSize: '14px', color: '#6c757d', paddingLeft: '0.5rem' }}>kayıt göster</span>
                             </div>
                           );
                         }
                       }}
                       className="p-datatable-sm tahsilat-table"
                       footerColumnGroup={footerGroup}
                       onPage={onPageChange}
                       onValueChange={(e) => setFiltrelenmisGozlemVerisi(e)}
                       stripedRows filterDisplay="row" showGridlines
                       scrollable
                       scrollDirection="horizontal"
                       emptyMessage="Tabloda herhangi bir veri mevcut değil">
              <Column field="tarih" header="TARİH" bodyClassName="ion-justify-content-center" headerClassName="ion-justify-content-center" />
              <Column field="tutar" header="TUTAR" bodyClassName="ion-justify-content-end" headerClassName="ion-justify-content-center" />
              <Column field="adiSoyadi" header="CARİ HESAP" headerClassName="ion-justify-content-center"
                      filter
                      filterElement={cariFilter}
                      showFilterMenu={false}
                      showFilterMatchModes={false}
                      showClearButton={false}
                      headerStyle={{ width: '75%' }} bodyStyle={{ width: '75%' }} />
            </DataTable>

          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  )
};

export default YoneticiGelirGozlem;
